import React, { useEffect, useState, useCallback, useRef } from "react";
import {toast, ToastContainer} from "react-toastify";
import EvaluationMainContent from "@components/evaluationmaincontent/EvaluationMainContent";
import EvaluationPageHeader from "@components/evaluationpageheader/EvaluationPageHeader";
import { EvaluationContainer, EvaluationFooter } from "@components/evaluation";
import type { EvaluationTaskResponse, QuestionWithEvaluation } from "@services/evaluationService";
import styles from "./EvaluatorPage.module.css";
import { useSelector } from "react-redux";
import ViewShortcuts from "@modals/viewshortcuts/ViewShortcuts";
import { useDispatch } from "react-redux";
import { setComponentToShow } from "@store/ModalToShow";
import { useShortcut } from "@hooks/useShortCut";

export interface EvaluationFormData {
  marksAwarded: number;
  feedback: string;
  verdict: string;
}

interface QuestionWiseProps {
  examSessionData: EvaluationTaskResponse | null;
  sessionName:string;
  onApproveAndNext: () => Promise<void>;
  onApproveAndSubmit: () => Promise<void>;
  onEvaluationDataChange: (data: EvaluationFormData) => void;
  isSaving?: boolean;
}

function QuestionWise({
  examSessionData,
  sessionName,
  onApproveAndNext,
  onApproveAndSubmit,
  onEvaluationDataChange,
  isSaving
}: QuestionWiseProps) {
  const [currentQuestion, setCurrentQuestion] = useState<QuestionWithEvaluation | null>(null);
  const [approvalValid, setApprovalValid] = useState(false);
  const [evaluationData, setEvaluationData] = useState<EvaluationFormData>({
    marksAwarded: 0,
    feedback: "",
    verdict:""
  });
  const toastShownRef = useRef<Set<string>>(new Set());
  const componentName = useSelector((state: any) => state.modalToShow.componentName);
  const dispatch=useDispatch();

  useEffect(() => {
    if (!examSessionData || !examSessionData.questions || examSessionData.questions.length === 0) {
      setCurrentQuestion(null);
      return;
    }
    const firstQuestion = examSessionData.questions[0];
    setCurrentQuestion(firstQuestion);

    const pages = firstQuestion.page_mapping;
    if (
          Array.isArray(pages) &&
          pages.length > 1
        ) {
          toast.info(
            `Answer on pages: ${pages.join(", ")}`,
            { autoClose: 4000 }
          );
        }
  }, [examSessionData]);

  const handleFormDataChange = useCallback((data: any, variant: any) => {
    console.log("QuestionWise - handleFormDataChange called:", { data, variant });
    
    // Handle both number and string values for marks
    const marksValue = data.marksAwarded || data.marks || 0;
    const marksAwarded = typeof marksValue === 'string' ? parseFloat(marksValue) || 0 : marksValue;
    
    const newEvaluationData = {
      marksAwarded: marksAwarded,
      feedback: data.feedback || data.remarks || "",
      verdict: data.verdict || data.evaluation_verdict || ""
    };
    
    setEvaluationData(newEvaluationData);
    onEvaluationDataChange(newEvaluationData);
  }, [onEvaluationDataChange]);

  const renderModal = () => {
    switch (componentName) {
      case "viewShortcuts":
        return (
          <ViewShortcuts isOpen={true} onClose={() => dispatch(setComponentToShow(""))}/>
        );
      default:
        return null;
    }
  };

   useShortcut("alt+/", () => {
        dispatch(setComponentToShow("viewShortcuts"));
      });

  return (
    <div className={styles.evaluationContainer}>
      <ToastContainer position="top-center" />
      {currentQuestion && examSessionData &&
        <div>
          <EvaluationPageHeader admissionID={examSessionData.exam_submission.submitted_by} 
            examSessionName={sessionName}
            onViewShortcutsClick={() => dispatch(setComponentToShow("viewShortcuts"))}/>
          <div className={styles.mainContainer}>
            <div className={styles.evaluatorMainContent}>
              <EvaluationMainContent 
                question={currentQuestion} 
                studentAnswerDocument={examSessionData.exam_submission.question_paper_submissions_documents[0]?.document}
              />
              <div className={styles.rightSidePanel}>
                <EvaluationContainer
                  question={currentQuestion}
                  onApprovalValidityChange={setApprovalValid}
                  onFormDataChange={handleFormDataChange}
                />
              </div>
            </div>
          </div>
          <EvaluationFooter
            totalQuestions={examSessionData.questions.length}
            approvalValid={approvalValid}
            evaluationType={examSessionData.evaluation_type}
            onApproveAndSubmit={onApproveAndSubmit}
            onApproveAndNext={onApproveAndNext}
            isSaving={isSaving}
          />
        </div>
      }
      {renderModal()}
    </div>
  );
}

export default QuestionWise;